import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Send, Trash2, ListTodo, Users } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import NotificationPanel from "./NotificationPanel";

export default function CommentSection({ relatedToType, relatedToId, relatedToName, projectId, projectName }) {
  const [newComment, setNewComment] = useState("");
  const [convertingCommentId, setConvertingCommentId] = useState(null);
  const [selectedUser, setSelectedUser] = useState("");
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ["comments", relatedToType, relatedToId],
    queryFn: () => base44.entities.Comment.filter({ 
      related_to_type: relatedToType, 
      related_to_id: relatedToId 
    }, "-created_date"),
    enabled: !!relatedToId,
    refetchInterval: 3000,
  });

  const createCommentMutation = useMutation({
    mutationFn: (data) => base44.entities.Comment.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", relatedToType, relatedToId] });
      queryClient.invalidateQueries({ queryKey: ["project-comments"] });
      setNewComment("");
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (id) => base44.entities.Comment.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", relatedToType, relatedToId] });
      queryClient.invalidateQueries({ queryKey: ["project-comments"] });
    },
  });

  const createTaskFromCommentMutation = useMutation({
    mutationFn: (data) => base44.entities.Task.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
      setConvertingCommentId(null);
      setSelectedUser("");
      toast.success("Oppgave opprettet fra kommentar");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    createCommentMutation.mutate({
      content: newComment,
      user_email: user?.email,
      user_name: user?.full_name,
      related_to_type: relatedToType,
      related_to_id: relatedToId,
      related_to_name: relatedToName,
    });
  };

  const handleConvertToTask = (comment) => {
    if (!selectedUser) {
      toast.error("Vennligst velg hvem som skal få oppgaven tildelt");
      return;
    }

    const assignedUser = users.find(u => u.email === selectedUser);
    const taskData = {
      title: comment.content.length > 100 ? comment.content.substring(0, 97) + "..." : comment.content,
      description: `Opprettet fra kommentar av ${comment.user_name}:\n\n${comment.content}`,
      status: "todo",
      priority: "medium",
      assigned_to: selectedUser,
      assigned_to_name: assignedUser?.full_name,
      project_id: projectId || null,
      project_name: projectName || null,
      related_to_type: relatedToType === "task" || relatedToType === "project" ? "none" : relatedToType,
      related_to_id: relatedToType === "task" || relatedToType === "project" ? null : relatedToId,
      related_to_name: relatedToType === "task" || relatedToType === "project" ? null : relatedToName,
    };

    createTaskFromCommentMutation.mutate(taskData);
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-zinc-900" />
          <h3 className="font-semibold text-slate-900">Kommentarer ({comments.length})</h3>
          <span className="text-xs text-emerald-600 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Sanntid
          </span>
        </div>
        <NotificationPanel 
          relatedToType={relatedToType}
          relatedToId={relatedToId}
          relatedToName={relatedToName}
        />
      </div>

      {/* Add Comment Form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <Textarea
          placeholder="Skriv en kommentar..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          rows={3}
          className="mb-2"
        />
        <div className="flex justify-end">
          <Button 
            type="submit" 
            disabled={!newComment.trim() || createCommentMutation.isPending}
            className="bg-zinc-900 hover:bg-zinc-900"
          >
            <Send className="w-4 h-4 mr-2" />
            Send
          </Button>
        </div>
      </form>

      {/* Comments List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="p-4 bg-slate-50 rounded-lg animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-1/4 mb-2" />
              <div className="h-3 bg-slate-200 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">Ingen kommentarer ennå</p>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => (
            <div key={comment.id} className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-lime-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-zinc-900">
                      {comment.user_name?.[0]?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{comment.user_name}</p>
                    <p className="text-xs text-slate-500">
                      {formatDistanceToNow(new Date(comment.created_date), { addSuffix: true, locale: nb })}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setConvertingCommentId(convertingCommentId === comment.id ? null : comment.id)}
                    title="Konverter til oppgave"
                  >
                    <ListTodo className="w-4 h-4 text-slate-400 hover:text-zinc-900" />
                  </Button>
                  {comment.user_email === user?.email && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => deleteCommentMutation.mutate(comment.id)}
                    >
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" />
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{comment.content}</p>
              
              {convertingCommentId === comment.id && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-600 mb-2 font-medium">Opprett oppgave fra denne kommentaren:</p>
                  <div className="flex gap-2">
                    <Select value={selectedUser} onValueChange={setSelectedUser}>
                      <SelectTrigger className="h-9 flex-1">
                        <SelectValue placeholder="Velg bruker å tildele" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.email}>
                            <div className="flex items-center gap-2">
                              <Users className="w-3 h-3" />
                              {u.full_name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      size="sm"
                      onClick={() => handleConvertToTask(comment)}
                      disabled={!selectedUser || createTaskFromCommentMutation.isPending}
                      className="bg-zinc-900 hover:bg-zinc-900"
                    >
                      <ListTodo className="w-4 h-4 mr-1" />
                      Opprett
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}